import React from 'react'
import { Alert, Button } from '@mui/material'
import { Link } from 'react-router-dom'
import { useGetBusiness } from '@marketing-milk/frontend'
import { CreativeImportJobStatus, CreativeImportRowStatus } from '@marketing-milk/interfaces'
import { useSprintPackets } from 'hooks/api/useSprintPackets'
import { CreativeImportState } from '../state'

interface Props {
  state: CreativeImportState
  onImportAnother: () => void
  onDone: () => void
}

export const SummaryStep = ({ state, onImportAnother, onDone }: Props) => {
  const business = useGetBusiness()
  // S6b: the sprint's packet, looked up rather than carried through state — one
  // packet per sprint, so this also resolves when a finished import is reopened
  // by URL (where the confirm response is long gone).
  const { list: packets } = useSprintPackets()
  const progress = state.progress
  if (!progress) return null

  const sprintPacket = progress.sprintID
    ? packets?.find(packet => packet.sprintID === progress.sprintID)
    : undefined

  const done = progress.rows.filter(row => row.status === CreativeImportRowStatus.Done)
  const failed = progress.rows.filter(row => row.status === CreativeImportRowStatus.Error)

  return (
    <div className="flex flex-col gap-4">
      {progress.status === CreativeImportJobStatus.Completed && (
        <Alert severity="success">
          {done.length} draft creative{done.length === 1 ? '' : 's'} imported.
        </Alert>
      )}
      {progress.status === CreativeImportJobStatus.CompletedWithErrors && (
        <Alert severity="warning">
          {done.length} of {progress.counts.selected} creatives imported; {failed.length} failed.
        </Alert>
      )}
      {progress.status === CreativeImportJobStatus.Failed && (
        <Alert severity="error">
          {progress.errorMessage ?? 'The import failed — no creatives were created.'}
        </Alert>
      )}
      {progress.status === CreativeImportJobStatus.Interrupted && (
        <Alert severity="warning">
          The import was interrupted — {done.length} of {progress.counts.selected} completed. The
          rest were not imported.
        </Alert>
      )}

      {done.length > 0 && (
        <div className="flex flex-col gap-1">
          <p className="font-medium">Imported drafts</p>
          {done.map(row =>
            row.createdAdCreativeDeleted ? (
              <p key={row.rowNumber} className="text-gray-500">
                {row.effectiveName} <span className="text-xs">(deleted)</span>
              </p>
            ) : (
              <Link
                key={row.rowNumber}
                to={`/${business.id}/creatives/${row.createdAdCreativeID}`}
                className="text-blue-600 hover:underline"
              >
                {row.effectiveName}
              </Link>
            )
          )}
          {progress.sprintID && (
            <p className="text-sm text-gray-600">
              Attached to{' '}
              <Link
                to={`/${business.id}/sprints/${progress.sprintID}`}
                className="text-blue-600 hover:underline"
              >
                the selected sprint
              </Link>
              .
            </p>
          )}
          {sprintPacket && (
            <p className="text-sm text-gray-600">
              Ready to prep in{' '}
              <Link
                to={`/${business.id}/sprint-packets/${sprintPacket.id}/edit`}
                className="text-blue-600 hover:underline"
              >
                the client packet
              </Link>
              .
            </p>
          )}
        </div>
      )}

      {failed.length > 0 && (
        <div className="flex flex-col gap-1">
          <p className="font-medium">Failed rows</p>
          {failed.map(row => (
            <p key={row.rowNumber} className="text-sm text-gray-700">
              <span className="font-medium">{row.effectiveName}</span> —{' '}
              {row.errorKind === 'fb' ? 'Facebook rejected it: ' : ''}
              {row.errorMessage ?? 'unknown error'}
            </p>
          ))}
          <p className="text-xs text-gray-500">
            Failed rows can be re-imported: fix the source if needed, re-upload the archive, and
            deselect the rows that already succeeded
            {done.length > 0 ? ` (${done.map(row => row.effectiveName).join(', ')})` : ''}.
          </p>
        </div>
      )}

      <div className="flex justify-between">
        <Button onClick={onImportAnother}>Import another</Button>
        <Button variant="contained" onClick={onDone}>
          Back to Creatives
        </Button>
      </div>
    </div>
  )
}
